// Page de dépôt d'annonce - Marketplace Particuliers
// Avec vérification d'identité pour lutter contre les arnaques
import { useState } from 'react';
import { useNavigate, useOutletContext, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Upload, X, Package, Wrench, Hammer, Box,
  ShoppingBag, MapPin, Euro, Camera, Check, Shield, UserCheck,
  BadgeCheck, Lock, FileText, Smartphone, ChevronRight, AlertCircle
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { toast } from 'sonner';
import UnifiedHeader from '../../../components/layout/UnifiedHeader';
import MainNavigation from '../../../components/layout/MainNavigation';

import { API_URL } from '../../../config/api';

const CATEGORIES = [
  { id: 'materiaux', label: 'Matériaux', icon: Package, description: 'Carrelage, parquet, isolant...' },
  { id: 'outils', label: 'Outils', icon: Wrench, description: 'Perceuse, meuleuse, scie...' },
  { id: 'equipements', label: 'Équipements', icon: Hammer, description: 'Échafaudage, bétonnière...' },
  { id: 'surplus_chantier', label: 'Surplus chantier', icon: Box, description: 'Restes neufs de chantier' },
  { id: 'occasion', label: 'Occasion', icon: ShoppingBag, description: 'Matériel d\'occasion' },
];

const CONDITIONS = [
  { id: 'neuf', label: 'Neuf', description: 'Jamais utilisé, emballage d\'origine' },
  { id: 'tres_bon_etat', label: 'Très bon état', description: 'Utilisé avec soin, comme neuf' },
  { id: 'bon_etat', label: 'Bon état', description: 'Quelques traces d\'usage normales' },
  { id: 'etat_correct', label: 'État correct', description: 'Usure visible mais fonctionnel' },
  { id: 'pour_pieces', label: 'Pour pièces', description: 'À réparer ou pour récupération' },
];

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PAGE DE CERTIFICATION - Pour non-connectés
// ══════════════════════════════════════════════════════════════════════════════
function CertificationPage({ onLogin }) {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F8FAFC] to-white">
      <UnifiedHeader />
      <MainNavigation />
      
      {/* Spacer Mobile - Compense les éléments fixed */}
      <div className="h-24 md:hidden" />
      
      {/* Spacer Desktop/Tablette - Compense header + nav + sous-menu */}
      <div className="hidden md:block h-32" />
      
      <main className="max-w-3xl mx-auto px-4 py-8 sm:py-12 relative">
        {/* Bouton fermer (X) - Retour à la page précédente */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 right-4 sm:top-8 sm:right-0 w-10 h-10 flex items-center justify-center rounded-full bg-white border border-gray-200 shadow-sm hover:bg-gray-50 hover:border-gray-300 transition-all z-10"
          aria-label="Fermer"
        >
          <X size={20} className="text-gray-500" />
        </button>
        
        {/* Header avec icône */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#1E40AF] to-[#3B82F6] mb-6 shadow-lg shadow-blue-500/20">
            <Shield size={40} className="text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
            Publie en toute sécurité
          </h1>
          <p className="text-gray-600 text-base sm:text-lg max-w-xl mx-auto">
            Pour lutter contre les arnaques sur internet, nous vérifions l'identité de chaque membre.
          </p>
        </motion.div>

        {/* Carte principale */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
        >
          {/* Bandeau vert */}
          <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-4 text-white">
            <div className="flex items-center gap-3">
              <BadgeCheck size={24} />
              <div>
                <p className="font-semibold">Une personne = Un compte vérifié</p>
                <p className="text-emerald-100 text-sm">Tous nos membres sont certifiés comme de vraies personnes</p>
              </div>
            </div>
          </div>

          <div className="p-6 sm:p-8">
            {/* Processus de vérification */}
            <h2 className="text-lg font-bold text-gray-900 mb-4">Comment ça marche ?</h2>
            
            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              <div className="text-center p-4">
                <div className="w-12 h-12 rounded-full bg-[#1E40AF]/10 flex items-center justify-center mx-auto mb-3">
                  <span className="text-[#1E40AF] font-bold">1</span>
                </div>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <FileText size={18} className="text-gray-500" />
                  <h3 className="font-semibold text-gray-900">Pièce d'identité</h3>
                </div>
                <p className="text-gray-500 text-sm">Photo de ta CNI ou passeport</p>
              </div>

              <div className="text-center p-4">
                <div className="w-12 h-12 rounded-full bg-[#1E40AF]/10 flex items-center justify-center mx-auto mb-3">
                  <span className="text-[#1E40AF] font-bold">2</span>
                </div>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Smartphone size={18} className="text-gray-500" />
                  <h3 className="font-semibold text-gray-900">Selfie de vérification</h3>
                </div>
                <p className="text-gray-500 text-sm">Pour confirmer que c'est bien toi</p>
              </div>

              <div className="text-center p-4">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                  <Check size={20} className="text-emerald-600" />
                </div>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <BadgeCheck size={18} className="text-emerald-600" />
                  <h3 className="font-semibold text-gray-900">Compte certifié</h3>
                </div>
                <p className="text-gray-500 text-sm">Publie tes annonces librement</p>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center mb-8">
              <Link to="/inscription">
                <Button className="h-14 px-8 bg-gradient-to-r from-[#F97316] to-[#EA580C] hover:from-[#EA580C] hover:to-[#DC2626] text-white rounded-xl font-bold text-lg shadow-lg shadow-orange-500/25 transition-all">
                  Inscris-toi gratuitement
                  <ChevronRight size={20} className="ml-2" />
                </Button>
              </Link>
              <p className="text-gray-500 text-sm mt-3">
                Déjà inscrit ? <Link to="/connexion" className="text-[#1E40AF] font-medium hover:underline">Connecte-toi</Link>
              </p>
            </div>

            {/* Avantages - Après le CTA */}
            <h2 className="text-lg font-bold text-gray-900 mb-4">Pourquoi nous vérifions les identités ?</h2>
            
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-xl">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Shield size={20} className="text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Protection contre les arnaques</h3>
                  <p className="text-gray-600 text-sm">Chaque vendeur est une personne réelle identifiée. Fini les faux profils et les escroqueries.</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-emerald-50 rounded-xl">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <UserCheck size={20} className="text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Confiance entre membres</h3>
                  <p className="text-gray-600 text-sm">Achetez et vendez l'esprit tranquille avec des personnes vérifiées.</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-orange-50 rounded-xl">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <Lock size={20} className="text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Transactions sécurisées</h3>
                  <p className="text-gray-600 text-sm">En cas de litige, nous pouvons identifier les parties prenantes.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer RGPD */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-100">
            <div className="flex items-start gap-3">
              <Lock size={16} className="text-gray-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-gray-500 leading-relaxed">
                <strong>Protection de tes données :</strong> Tes documents d'identité sont traités de manière sécurisée et conforme au RGPD. 
                Ils sont utilisés uniquement pour la vérification de ton identité et ne sont jamais partagés avec des tiers. 
                Tu peux demander leur suppression à tout moment. 
                <Link to="/confidentialite" className="text-[#1E40AF] hover:underline ml-1">En savoir plus</Link>
              </p>
            </div>
          </div>
        </motion.div>

        {/* Badges de confiance */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-8 flex flex-wrap items-center justify-center gap-6 text-gray-400"
        >
          <div className="flex items-center gap-2">
            <Shield size={18} />
            <span className="text-sm">Données sécurisées</span>
          </div>
          <div className="flex items-center gap-2">
            <Lock size={18} />
            <span className="text-sm">Conforme RGPD</span>
          </div>
          <div className="flex items-center gap-2">
            <BadgeCheck size={18} />
            <span className="text-sm">100% vérifiés</span>
          </div>
        </motion.div>
      </main>
    </div>
  );
}

export default function CreateListingPage() {
  const navigate = useNavigate();
  const { profile } = useOutletContext() || {};
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const token = localStorage.getItem('ecosystem_token');

  const [form, setForm] = useState({
    title: '',
    description: '',
    category: '',
    condition: '',
    price: '',
    price_negotiable: false,
    city: profile?.city || '',
    postal_code: profile?.postal_code || '',
    delivery_available: false,
    photos: []
  });

  // ══════════════════════════════════════════════════════════════════════════════
  // SI NON CONNECTÉ → Afficher la page de certification
  // ══════════════════════════════════════════════════════════════════════════════
  if (!token) {
    return <CertificationPage />;
  }

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + form.photos.length > 5) {
      toast.error('Maximum 5 photos autorisées');
      return;
    }

    // For now, create object URLs for preview
    // In production, would upload to cloud storage
    const newPhotos = files.map(file => URL.createObjectURL(file));
    setForm({ ...form, photos: [...form.photos, ...newPhotos] });
  };

  const removePhoto = (index) => {
    const newPhotos = form.photos.filter((_, i) => i !== index);
    setForm({ ...form, photos: newPhotos });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.title || !form.description || !form.category || !form.condition || !form.price || !form.city) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSubmitting(true);
    try {
      // For demo, use placeholder images if no photos uploaded
      const photos = form.photos.length > 0 ? form.photos : [
        'https://images.unsplash.com/photo-1581147036324-c17ac41f05b1?w=800'
      ];

      const res = await fetch(`${API_URL}/api/ecosystem/listings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...form,
          price: parseFloat(form.price),
          photos: photos
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success('Annonce publiée avec succès !');
        navigate(`/marketplace/annonce/${data.listing_id}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de la publication');
      }
    } catch (e) {
      console.error('Error creating listing:', e);
      toast.error('Erreur de connexion');
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return form.category && form.condition;
      case 2:
        return form.title && form.description && form.price;
      case 3:
        return form.city && form.postal_code;
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={24} />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-gray-900">Déposer une annonce</h1>
            <p className="text-sm text-gray-500">Étape {step}/3</p>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="h-1 bg-gray-100">
          <div
            className="h-full bg-green-500 transition-all duration-300"
            style={{ width: `${(step / 3) * 100}%` }}
          />
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Step 1: Category & Condition */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-8"
          >
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Catégorie</h2>
              <p className="text-gray-600 mb-4">Sélectionne la catégorie de ton article</p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setForm({ ...form, category: cat.id })}
                    className={`flex items-start gap-4 p-4 rounded-xl border-2 text-left transition-all ${
                      form.category === cat.id
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`category-${cat.id}`}
                  >
                    <div className={`p-3 rounded-lg ${
                      form.category === cat.id ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600'
                    }`}>
                      <cat.icon size={24} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{cat.label}</p>
                      <p className="text-sm text-gray-500">{cat.description}</p>
                    </div>
                    {form.category === cat.id && (
                      <Check size={20} className="text-green-500 ml-auto" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">État</h2>
              <p className="text-gray-600 mb-4">Dans quel état se trouve ton article ?</p>
              
              <div className="space-y-2">
                {CONDITIONS.map((cond) => (
                  <button
                    key={cond.id}
                    onClick={() => setForm({ ...form, condition: cond.id })}
                    className={`flex items-center gap-4 w-full p-4 rounded-xl border-2 text-left transition-all ${
                      form.condition === cond.id
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`condition-${cond.id}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      form.condition === cond.id ? 'border-green-500 bg-green-500' : 'border-gray-300'
                    }`}>
                      {form.condition === cond.id && <Check size={14} className="text-white" />}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{cond.label}</p>
                      <p className="text-sm text-gray-500">{cond.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 2: Details */}
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Détails de l'annonce</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Titre de l'annonce *
                  </label>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Ex: Perceuse visseuse Makita 18V"
                    maxLength={80}
                    data-testid="listing-title-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">{form.title.length}/80</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                  </label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Décris ton article en détail : état, dimensions, marque, raison de la vente..."
                    rows={6}
                    maxLength={2000}
                    data-testid="listing-description-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">{form.description.length}/2000</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prix *
                  </label>
                  <div className="relative">
                    <Euro className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <Input
                      type="number"
                      value={form.price}
                      onChange={(e) => setForm({ ...form, price: e.target.value })}
                      placeholder="0"
                      className="pl-10"
                      min="0"
                      data-testid="listing-price-input"
                    />
                  </div>
                  
                  <label className="flex items-center gap-2 mt-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.price_negotiable}
                      onChange={(e) => setForm({ ...form, price_negotiable: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-700">Prix négociable</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Photos (max 5)
                  </label>
                  
                  <div className="flex flex-wrap gap-3">
                    {form.photos.map((photo, idx) => (
                      <div key={idx} className="relative w-24 h-24 rounded-lg overflow-hidden bg-gray-100">
                        <img src={photo} alt="" className="w-full h-full object-cover" />
                        <button
                          onClick={() => removePhoto(idx)}
                          className="absolute top-1 right-1 bg-black/50 text-white p-1 rounded-full"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                    
                    {form.photos.length < 5 && (
                      <label className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-green-500 hover:bg-green-50 transition-colors">
                        <Camera size={24} className="text-gray-400" />
                        <span className="text-xs text-gray-500 mt-1">Ajouter</span>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handlePhotoUpload}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Ajoutez jusqu'à 5 photos. La première sera l'image principale.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 3: Location & Options */}
        {step === 3 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Localisation</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ville *
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <Input
                      value={form.city}
                      onChange={(e) => setForm({ ...form, city: e.target.value })}
                      placeholder="Paris"
                      className="pl-10"
                      data-testid="listing-city-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code postal *
                  </label>
                  <Input
                    value={form.postal_code}
                    onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                    placeholder="75000"
                    maxLength={5}
                    data-testid="listing-postal-input"
                  />
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Options</h2>
              
              <label className="flex items-start gap-3 p-4 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={form.delivery_available}
                  onChange={(e) => setForm({ ...form, delivery_available: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500 mt-0.5"
                />
                <div>
                  <p className="font-medium text-gray-900">Livraison possible</p>
                  <p className="text-sm text-gray-500">Je peux envoyer cet article par la poste ou un transporteur</p>
                </div>
              </label>
            </div>

            {/* Summary */}
            <div className="bg-green-50 rounded-xl p-4 border border-green-200">
              <h3 className="font-semibold text-gray-900 mb-3">Récapitulatif</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Catégorie</span>
                  <span className="font-medium">{CATEGORIES.find(c => c.id === form.category)?.label}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">État</span>
                  <span className="font-medium">{CONDITIONS.find(c => c.id === form.condition)?.label}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Prix</span>
                  <span className="font-medium text-green-600">{form.price} €</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Localisation</span>
                  <span className="font-medium">{form.city} ({form.postal_code})</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Navigation Buttons */}
        <div className="flex gap-4 mt-8">
          {step > 1 && (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="flex-1"
            >
              Retour
            </Button>
          )}
          
          {step < 3 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="flex-1 bg-green-600 hover:bg-green-700"
              data-testid="next-step-btn"
            >
              Continuer
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!canProceed() || submitting}
              className="flex-1 bg-green-600 hover:bg-green-700"
              data-testid="publish-listing-btn"
            >
              {submitting ? 'Publication...' : 'Publier l\'annonce'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
